import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Lightbulb, AlertTriangle, Calendar, UserPlus, ShieldAlert, Bell, Loader2 } from 'lucide-react';
import { useMarkAllRead, useMarkNotificationRead, useNotifications } from '../../hooks/useNotifications';
import type { Notification, NotificationType } from '../../api/notifications';

interface Props {
  onClose?: () => void;
}

const TYPE_ICON: Record<NotificationType, { icon: typeof Bell; badgeClass: string }> = {
  audit_completed:         { icon: ClipboardCheck, badgeClass: 'gp-icon-badge--indigo' },
  recommendations_ready:   { icon: Lightbulb,      badgeClass: 'gp-icon-badge--orange' },
  plan_limit_warning:      { icon: AlertTriangle,  badgeClass: 'gp-icon-badge--pink' },
  weekly_digest:           { icon: Calendar,        badgeClass: 'gp-icon-badge--blue' },
  team_invitation:         { icon: UserPlus,        badgeClass: 'gp-icon-badge--purple' },
  fraud_order_flagged:     { icon: ShieldAlert,      badgeClass: 'gp-icon-badge--pink' },
};

// Where clicking a notification of this type should take the merchant —
// matches the standard "click a notification, land on the relevant content"
// pattern (Gmail, Slack, etc.); previously clicking only marked it read.
const TYPE_LINK: Record<NotificationType, string> = {
  audit_completed:       '/audits',
  recommendations_ready: '/recommendations',
  plan_limit_warning:    '/billing',
  weekly_digest:         '/',
  team_invitation:       '/team',
  fraud_order_flagged:   '/fraud-protection',
};

function typeMeta(type: NotificationType) {
  return TYPE_ICON[type] ?? { icon: Bell, badgeClass: 'gp-icon-badge--indigo' };
}

export function NotificationList({ onClose }: Props) {
  const { data, isLoading } = useNotifications({ per_page: 30 });
  const { mutate: markAllRead, isPending: markingAll } = useMarkAllRead();

  const handleMarkAll = useCallback(() => {
    markAllRead(undefined, { onSuccess: onClose });
  }, [markAllRead, onClose]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 size={20} className="animate-spin text-text-muted" />
      </div>
    );
  }

  const notifications = data?.data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <span className="text-[15px] font-semibold text-text-primary">Notifications</span>
        {notifications.some((n) => !n.is_read) && (
          <button
            type="button"
            onClick={handleMarkAll}
            disabled={markingAll}
            className="text-[13px] font-medium text-primary hover:text-primary-hover disabled:opacity-50"
          >
            {markingAll ? 'Marking…' : 'Mark all as read'}
          </button>
        )}
      </div>

      <div className="border-t border-border">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <Bell size={22} className="text-text-muted" />
            <span className="text-[14px] text-text-secondary">No notifications yet.</span>
          </div>
        ) : (
          notifications.map((n, idx) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onClose={onClose}
              isLast={idx === notifications.length - 1}
            />
          ))
        )}
      </div>
    </div>
  );
}

function NotificationItem({
  notification: n,
  onClose,
  isLast,
}: {
  notification: Notification;
  onClose?: () => void;
  isLast: boolean;
}) {
  const { mutate: markRead, isPending } = useMarkNotificationRead();
  const { icon: Icon, badgeClass } = typeMeta(n.type);
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    const goToLink = () => {
      const path = TYPE_LINK[n.type];
      if (path) navigate(path);
      onClose?.();
    };

    if (!n.is_read) {
      markRead(n.id, { onSuccess: goToLink });
    } else {
      goToLink();
    }
  }, [n.id, n.is_read, n.type, markRead, navigate, onClose]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-bg disabled:opacity-60 ${
        !isLast ? 'border-b border-border' : ''
      } ${!n.is_read ? 'bg-primary-subtle/30' : ''}`}
    >
      <span className={`gp-icon-badge ${badgeClass}`}>
        <Icon size={16} />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-start justify-between gap-2">
          <span className={`text-[14px] leading-5 ${!n.is_read ? 'font-semibold text-text-primary' : 'font-medium text-text-secondary'}`}>
            {n.title}
          </span>
          {!n.is_read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
        </span>
        <span className="text-[13px] leading-5 text-text-secondary">
          {n.message}
        </span>
        <span className="text-[12px] leading-4 text-text-muted">
          {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
        </span>
      </span>
    </button>
  );
}
